import { OCTANT_DIRECTIONS, type Point2, distance } from './point';

/**
 * Centre of the circle through three points, or `null` when they are collinear.
 *
 * How an arc's centre is recovered at all: the wire format flattens curves to
 * polylines, so an ARC arrives as a run of points with no centre attached. Three
 * points on it determine the circle exactly, which makes a centre snap on a
 * flattened arc as accurate as one on a real arc entity.
 */
export const circumcenter = (
  a: Point2,
  b: Point2,
  c: Point2,
): Point2 | null => {
  const [ax, ay] = a;
  const [bx, by] = b;
  const [cx, cy] = c;

  // Twice the signed area of the triangle; zero means the points are collinear
  // and there is no finite circle through them.
  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
  if (!Number.isFinite(d) || Math.abs(d) < Number.EPSILON) {
    return null;
  }

  const aSq = ax * ax + ay * ay;
  const bSq = bx * bx + by * by;
  const cSq = cx * cx + cy * cy;

  const centerX = (aSq * (by - cy) + bSq * (cy - ay) + cSq * (ay - by)) / d;
  const centerY = (aSq * (cx - bx) + bSq * (ax - cx) + cSq * (bx - ax)) / d;

  return Number.isFinite(centerX) && Number.isFinite(centerY)
    ? [centerX, centerY]
    : null;
};

/**
 * How far the centre may sit from its samples, as a multiple of their spread,
 * before the answer is treated as noise rather than a centre.
 *
 * Three nearly-collinear points have a real circumcenter, but an enormous and
 * meaningless one — a flat run of a tessellated curve yields a "centre" thousands
 * of units away. Rejecting those is what stops a snap target appearing in empty
 * space far from anything the user drew.
 */
const MAX_RADIUS_RATIO = 1000;

/** Fraction of a run's own extent within which two points are the same point. */
const CLOSURE_TOLERANCE = 1e-6;

/**
 * True when the run comes back to where it started.
 *
 * Compared against the run's own extent rather than exactly. A tessellated full
 * circle ends at `sin(2π)`, which is -2.4e-16 rather than 0, so its last point is
 * only *nearly* its first — and treating that as open leaves the first and last
 * samples a hair apart, which is precisely the degenerate case a circumcenter
 * cannot survive.
 */
const isClosedRun = (points: readonly Point2[]): boolean => {
  const first = points[0];
  const last = points[points.length - 1];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  points.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });

  const extent = Math.max(maxX - minX, maxY - minY);
  const tolerance = extent > 0 ? extent * CLOSURE_TOLERANCE : 0;
  return Math.hypot(last[0] - first[0], last[1] - first[1]) <= tolerance;
};

/**
 * Centre of the circle a flattened arc or circle came from, or `null`.
 *
 * Exact for a circle or a circular arc however finely it was tessellated. Only
 * meaningful for genuinely circular geometry — see `flattenedShapeCenter`.
 *
 * Samples at thirds rather than start/middle/end. On a closed run the two ends sit
 * on top of each other, and even on an open one they can be close; thirds keeps
 * the three samples as far apart as the run allows, which is what keeps the
 * circumcenter well conditioned.
 */
export const flattenedCurveCenter = (
  points: readonly Point2[],
): Point2 | null => {
  if (points.length < 3) {
    return null;
  }

  const usable = isClosedRun(points) ? points.slice(0, -1) : points;
  if (usable.length < 3) {
    return null;
  }

  const a = usable[0];
  const b = usable[Math.floor(usable.length / 3)];
  const c = usable[Math.floor((2 * usable.length) / 3)];
  const center = circumcenter(a, b, c);
  if (!center) {
    return null;
  }

  const spread = Math.max(
    Math.hypot(b[0] - a[0], b[1] - a[1]),
    Math.hypot(c[0] - b[0], c[1] - b[1]),
  );
  const radius = Math.hypot(center[0] - a[0], center[1] - a[1]);
  return spread > 0 && radius > spread * MAX_RADIUS_RATIO ? null : center;
};

export type Circle2 = { readonly center: Point2; readonly radius: number };

/** A circle's rim points: top, bottom, sides and diagonals, anticlockwise from +X. */
export const circleRimPoints = ({
  center: [cx, cy],
  radius,
}: Circle2): Point2[] =>
  OCTANT_DIRECTIONS.map(([ux, uy]) => [cx + ux * radius, cy + uy * radius]);

/**
 * The one circle that `circles` all are, or `null` when they differ: every
 * centre and radius must sit within `radiusFraction` of the first circle's
 * radius. The answer is their mean, so no single fit's rounding wins.
 */
export const commonCircle = (
  circles: readonly Circle2[],
  radiusFraction: number,
): Circle2 | null => {
  const [first] = circles;
  if (!first) {
    return null;
  }
  const tolerance = first.radius * radiusFraction;
  const isSameCircle = circles.every(
    ({ center, radius }) =>
      distance(center, first.center) <= tolerance &&
      Math.abs(radius - first.radius) <= tolerance,
  );
  if (!isSameCircle) {
    return null;
  }

  const mean = (pick: (circle: Circle2) => number) =>
    circles.reduce((total, circle) => total + pick(circle), 0) / circles.length;
  return {
    center: [mean(({ center }) => center[0]), mean(({ center }) => center[1])],
    radius: mean(({ radius }) => radius),
  };
};

/**
 * The circle a flattened arc or circle came from, or `null`. Fitted like
 * `flattenedCurveCenter`, so only meaningful for genuinely circular geometry.
 */
const flattenedCurveCircle = (points: readonly Point2[]): Circle2 | null => {
  const center = flattenedCurveCenter(points);
  if (!center) {
    return null;
  }
  const [x, y] = points[0];
  return { center, radius: Math.hypot(x - center[0], y - center[1]) };
};

/**
 * The circle every point of a flattened run lies on, or `null`.
 *
 * `flattenedCurveCenter` fits from three points only, and the vertices of any
 * regular polygon share a circumcircle, so on its own it cannot tell a curve
 * from a polygon. Here every point must sit within `radiusFraction` of the
 * fitted radius.
 */
export const flattenedRunCircle = (
  points: readonly Point2[],
  radiusFraction: number,
): Circle2 | null => {
  const circle = flattenedCurveCircle(points);
  if (!circle) {
    return null;
  }
  const { center, radius } = circle;
  const tolerance = radius * radiusFraction;
  const isOnCircle = points.every(
    ([px, py]) =>
      Math.abs(Math.hypot(px - center[0], py - center[1]) - radius) <=
      tolerance,
  );
  return isOnCircle ? circle : null;
};

/**
 * Centre of a closed run's extent.
 *
 * Exact for anything symmetric about its own middle — a full ellipse, most
 * notably, whose centre a circumcenter cannot find because three points on an
 * ellipse do not determine it.
 */
export const flattenedShapeCenter = (
  points: readonly Point2[],
): Point2 | null => {
  if (points.length < 3) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  points.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });
  return Number.isFinite(minX) ? [(minX + maxX) / 2, (minY + maxY) / 2] : null;
};
